import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getLanguage, t } from '../../i18n'

// The store's own changes made in the website editor (Website > Edit live),
// applied on top of whichever template it uses:
//   texts:  "<lang>|<original text>" -> new text
//   hidden: items to hide, matched by tag, class, start of text and image
//   colors: primary / accent / background / text (applied in CustomerLayout)
// Texts are changed in place (never adding or removing nodes React owns), and
// hidden items only get an attribute, so React keeps working normally.

// Inside the editor's preview frame (it is named, and the name survives
// navigation inside the frame).
export const EDITOR_FRAME = 'site-editor-preview'
export const inEditor = () => typeof window !== 'undefined' && window.name === EDITOR_FRAME && window.parent !== window

const norm = s => String(s || '').replace(/\s+/g, ' ').trim()
const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'svg', 'SVG', 'path', 'IFRAME'])

function textNodes(el) {
  const list = []
  for (const node of el.childNodes) if (node.nodeType === 3 && node.nodeValue.trim()) list.push(node)
  return list
}
export const directText = el => norm(textNodes(el).map(n => n.nodeValue).join(' '))

function setDirectText(el, value) {
  const nodes = textNodes(el)
  if (!nodes.length) return
  if (nodes[0].nodeValue !== value) nodes[0].nodeValue = value
  for (const node of nodes.slice(1)) if (node.nodeValue) node.nodeValue = ''
}

// What identifies an item to hide (also shown in the editor's list).
export function itemSignature(el) {
  return {
    tag: el.tagName.toLowerCase(),
    cls: el.getAttribute('class') || '',
    text: norm(el.textContent).slice(0, 80),
    src: el.getAttribute('src') || '',
    label: norm(el.textContent).slice(0, 60) || el.getAttribute('alt') || el.tagName.toLowerCase(),
  }
}
const sameItem = (el, h) => el.tagName.toLowerCase() === h.tag && (el.getAttribute('class') || '') === h.cls
  && norm(el.textContent).slice(0, 80) === h.text && (el.getAttribute('src') || '') === h.src

// The text an element showed before any edit (React may change it later,
// e.g. a new language: then the new text becomes the original).
export function originalText(el) {
  const now = directText(el)
  if (el.__seOriginal === undefined || (el.__seApplied !== undefined ? now !== el.__seApplied : now !== el.__seOriginal)) {
    el.__seOriginal = now
    el.__seApplied = undefined
  }
  return el.__seOriginal
}

export function applyEdits(root, edits) {
  if (!root) return
  const texts = edits?.texts || {}
  const hidden = edits?.hidden || []
  const lang = getLanguage()
  const elements = root.querySelectorAll('*')
  for (const el of elements) {
    if (SKIP.has(el.tagName) || el.closest('[data-se-ui]')) continue
    if (!textNodes(el).length) continue
    const original = originalText(el)
    if (!original) continue
    const replacement = texts[`${lang}|${original}`]
    if (replacement !== undefined) {
      setDirectText(el, replacement)
      el.__seApplied = norm(replacement)
    } else if (el.__seApplied !== undefined) {
      setDirectText(el, original)
      el.__seApplied = undefined
    }
  }
  if (hidden.length || root.querySelector('[data-se-hidden]')) {
    for (const el of root.querySelectorAll('*')) {
      if (el.closest('[data-se-ui]')) continue
      const hide = hidden.some(h => sameItem(el, h))
      if (hide && !el.hasAttribute('data-se-hidden')) el.setAttribute('data-se-hidden', '')
      else if (!hide && el.hasAttribute('data-se-hidden')) el.removeAttribute('data-se-hidden')
    }
  }
}

// Keeps the edits applied while the page renders and changes.
export default function SiteEdits({ edits, rootRef }) {
  const editsRef = useRef(edits)
  editsRef.current = edits
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    // Applied right after each render, before the browser paints, so visitors
    // never see the original text flash (and it works in background tabs).
    let queued = false
    const run = () => { queued = false; applyEdits(root, editsRef.current) }
    const schedule = () => { if (!queued) { queued = true; queueMicrotask(run) } }
    run()
    const observer = new MutationObserver(schedule)
    observer.observe(root, { subtree: true, childList: true, characterData: true })
    return () => observer.disconnect()
  }, [rootRef])
  useEffect(() => { applyEdits(rootRef.current, edits) }, [edits, rootRef])
  return inEditor() ? <EditorOverlay rootRef={rootRef} /> : null
}

// In the editor frame: hover to outline, click to select, then change the
// text or hide the item. Every change goes to the editor page, which keeps
// the draft and sends it back to be shown here.
function EditorOverlay({ rootRef }) {
  const [mode, setMode] = useState('edit')
  const [hoverBox, setHoverBox] = useState(null)
  const [selected, setSelected] = useState(null)
  const [draft, setDraft] = useState('')
  const [, setTick] = useState(0)
  const location = useLocation()
  const post = message => window.parent.postMessage(message, window.location.origin)

  useEffect(() => {
    const onMessage = e => {
      if (e.origin !== window.location.origin || e.source !== window.parent) return
      if (e.data?.type === 'se:mode') { setMode(e.data.mode); setSelected(null); setHoverBox(null) }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])
  // Tells the editor which page is showing (also after browsing inside the frame).
  useEffect(() => {
    setSelected(null)
    post({ type: 'se:ready', path: location.pathname + location.search })
  }, [location.pathname, location.search])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    root.setAttribute('data-se-editing', '')
    if (mode !== 'edit') { root.removeAttribute('data-se-editing'); return }
    const pick = target => (target instanceof Element && !target.closest('[data-se-ui]') && root.contains(target) ? target : null)
    const onOver = e => { const el = pick(e.target); setHoverBox(el ? el.getBoundingClientRect() : null) }
    const onClick = e => {
      const el = pick(e.target)
      if (!el) return
      e.preventDefault()
      e.stopPropagation()
      setSelected(el)
      setDraft(directText(el))
    }
    const block = e => { if (pick(e.target)) { e.preventDefault(); e.stopPropagation() } }
    document.addEventListener('mouseover', onOver, true)
    document.addEventListener('click', onClick, true)
    document.addEventListener('submit', block, true)
    document.addEventListener('auxclick', block, true)
    const onScroll = () => setTick(n => n + 1)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      root.removeAttribute('data-se-editing')
      document.removeEventListener('mouseover', onOver, true)
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('submit', block, true)
      document.removeEventListener('auxclick', block, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [mode, rootRef])

  if (mode !== 'edit') return null
  const box = selected && document.contains(selected) ? selected.getBoundingClientRect() : null
  const hasText = selected && textNodes(selected).length > 0
  const isHidden = selected?.hasAttribute('data-se-hidden')
  const parent = selected?.parentElement && rootRef.current?.contains(selected.parentElement) && selected.parentElement !== rootRef.current ? selected.parentElement : null

  const applyText = () => {
    const original = originalText(selected)
    post({ type: 'se:text', lang: getLanguage(), original, value: draft.trim() })
  }
  const toggleHidden = () => {
    post({ type: isHidden ? 'se:show' : 'se:hide', item: itemSignature(selected) })
    setSelected(null)
  }

  // Keep the toolbar on screen, below the item when there is room.
  const toolbarTop = box ? Math.min(Math.max(8, box.bottom + 8), window.innerHeight - 190) : 0
  const toolbarLeft = box ? Math.min(Math.max(8, box.left), window.innerWidth - 328) : 0

  return (
    <div data-se-ui="" dir="ltr">
      {hoverBox && <div className="se-outline" style={{ top: hoverBox.top, left: hoverBox.left, width: hoverBox.width, height: hoverBox.height }} />}
      {box && <div className="se-outline se-outline-selected" style={{ top: box.top, left: box.left, width: box.width, height: box.height }} />}
      {box && (
        <div className="se-toolbar" style={{ top: toolbarTop, left: toolbarLeft }} role="dialog" aria-label={t('Edit item')} dir={document.documentElement.dir || 'ltr'}>
          <div className="se-toolbar-head">
            <span>{hasText ? t('Text') : t('Item')}{isHidden ? ` · ${t('hidden')}` : ''}</span>
            <button type="button" onClick={() => setSelected(null)} aria-label={t('Close')}>×</button>
          </div>
          {hasText && (
            <>
              <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={Math.min(4, Math.max(1, Math.ceil(draft.length / 38)))} aria-label={t('Text')} />
              <button type="button" className="se-primary" onClick={applyText} disabled={!draft.trim()}>{t('Apply text')}</button>
            </>
          )}
          <div className="se-row">
            <button type="button" onClick={toggleHidden}>{isHidden ? t('Show again') : t('Hide')}</button>
            {parent && <button type="button" onClick={() => { setSelected(parent); setDraft(directText(parent)) }}>{t('Select bigger area')}</button>}
          </div>
        </div>
      )}
    </div>
  )
}
